import React, { useState } from "react";
import {
  Card,
  Input,
  Button,
  Select,
  Spin,
  Typography,
  Alert,
  Checkbox,
} from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  BookOutlined,
} from "@ant-design/icons";
import Layout from "../../components/Layout/Layout";
import api, { getCurrentUserId } from "../../services/api";
import "./TeacherAgents.css";

const { Title, Text } = Typography;
const { Option } = Select;

const LessonPlanGenerator = () => {
  const [form, setForm] = useState({
    subject: "",
    topic: "",
    grade: "",
    duration: 40,
    includeIllustrations: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lessonPackage, setLessonPackage] = useState(null);

  const subjects = [
    "Mathematics",
    "English",
    "Science",
    "Biology",
    "Chemistry",
    "Physics",
    "Basic Science",
    "History",
    "Geography",
    "Computer Science",
    "Economics",
    "Agriculture",
  ];

  const grades = ["JSS1", "JSS2", "JSS3", "SS1", "SS2", "SS3"];

  const handleInputChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async () => {
    if (!form.subject || !form.topic || !form.grade) {
      setError("Please fill all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post("/api/agents/teacher-support/assist", {
        teacher_id: localStorage.getItem("teacher_id") || getCurrentUserId(),
        request_type: "lesson_plan",
        details: {
          subject: form.subject,
          topic: form.topic,
          grade: form.grade,
          duration_mins: form.duration,
          include_illustration: form.includeIllustrations,
        },
      });

      if (response.data.success) {
        setLessonPackage(response.data.response);
      } else {
        setError("Failed to generate lesson plan");
      }
    } catch (err) {
      console.error("Lesson plan generation error:", err);
      setError("An error occurred while generating the lesson plan");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (filename, displayName) => {
    if (!filename) return;

    setLoading(true);

    // Import the file downloader utility
    import("../../utils/fileDownloader")
      .then((module) => {
        module.validateAndDownload(
          filename,
          displayName,
          (errorMsg) => setError(errorMsg),
          setLoading
        );
      })
      .catch((err) => {
        console.error("Error loading file downloader:", err);
        setError(`Failed to load download utility: ${err.message}`);
        setLoading(false);

        // Fallback to simple window.open if module fails to load
        try {
          const baseFilename = filename.split(/[\/\\]/).pop();
          window.open(`/api/download/${baseFilename}`, "_blank");
        } catch (e) {
          console.error("Download error:", e);
          setError(`Failed to download ${displayName}`);
        }
      });
  };

  return (
    <Layout>
      <div className="teacher-agent-container">
        <Card
          className="teacher-agent-card"
          title={
            <div className="agent-header">
              <Title level={3}>Lesson Plan Generator</Title>
            </div>
          }
        >
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              className="error-alert"
              onClose={() => setError(null)}
            />
          )}

          {!lessonPackage ? (
            <div className="lesson-plan-form">
              <div className="form-group">
                <label>Subject*</label>
                <Select
                  placeholder="Select a subject"
                  style={{ width: "100%" }}
                  value={form.subject}
                  onChange={(value) => handleInputChange("subject", value)}
                >
                  {subjects.map((subject) => (
                    <Option key={subject} value={subject}>
                      {subject}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="form-group">
                <label>Topic*</label>
                <Input
                  placeholder="Enter the topic (e.g., Photosynthesis, Fractions, etc.)"
                  value={form.topic}
                  onChange={(e) => handleInputChange("topic", e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Grade Level*</label>
                <Select
                  placeholder="Select grade level"
                  style={{ width: "100%" }}
                  value={form.grade}
                  onChange={(value) => handleInputChange("grade", value)}
                >
                  {grades.map((grade) => (
                    <Option key={grade} value={grade}>
                      {grade}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="form-group">
                <label>Duration (minutes)</label>
                <Select
                  style={{ width: "100%" }}
                  value={form.duration}
                  onChange={(value) => handleInputChange("duration", value)}
                >
                  <Option value={30}>30 minutes</Option>
                  <Option value={40}>40 minutes</Option>
                  <Option value={60}>60 minutes</Option>
                  <Option value={90}>90 minutes</Option>
                </Select>
              </div>

              <div className="form-group checkbox-group">
                <Checkbox
                  checked={form.includeIllustrations}
                  onChange={(e) =>
                    handleInputChange("includeIllustrations", e.target.checked)
                  }
                >
                  Include illustrations and diagrams
                </Checkbox>
              </div>

              <Button
                type="primary"
                className="submit-button"
                onClick={handleSubmit}
                loading={loading}
              >
                {loading ? "Generating..." : "Generate Lesson Plan"}
              </Button>
            </div>
          ) : (
            <div className="lesson-plan-result">
              <div className="result-header">
                <Title level={4}>
                  {form.subject} - {form.topic} ({form.grade})
                </Title>
                <Text type="secondary">Duration: {form.duration} minutes</Text>
              </div>

              <div className="download-options">
                <Card className="download-card">
                  <FileTextOutlined className="download-icon" />
                  <div className="download-info">
                    <Title level={5}>Lesson Plan</Title>
                    <Text>
                      Complete teacher's guide with objectives, activities and
                      assessment
                    </Text>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        handleDownload(
                          lessonPackage.downloads?.lesson_plan_docx,
                          "Lesson Plan"
                        )
                      }
                    >
                      Download Plan (.docx)
                    </Button>
                  </div>
                </Card>

                <Card className="download-card">
                  <BookOutlined className="download-icon" />
                  <div className="download-info">
                    <Title level={5}>Student Notes</Title>
                    <Text>
                      Comprehensive notes with key concepts, examples and
                      practice questions
                    </Text>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        handleDownload(
                          lessonPackage.downloads?.notes_docx,
                          "Student Notes"
                        )
                      }
                    >
                      Download Notes (.docx)
                    </Button>
                  </div>
                </Card>
              </div>

              <div className="action-buttons">
                <Button onClick={() => setLessonPackage(null)}>
                  Create New Lesson Plan
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default LessonPlanGenerator;
